import { Entity, ThemeExposure, ThemeSchemaResponse } from '@api';
import { SkeletonLoader } from '@design';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { FollowThemeButton } from '@terminal/components/follow-theme-button/FollowThemeButton';
import { ThemeHeaderDetailed } from '@terminal/components/theme-header-detailed/ThemeHeaderDetailed';
import { useThemeCompanies } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { ThemePerformanceSection } from '@terminal/views/theme-overview/theme-overview-drawer/components/theme-performance-section/ThemePerformanceSection';
import React from 'react';

import { StepperSection } from '../components/stepper-section/StepperSection';
import { TopCompaniesSection } from './components/top-companies-section/TopCompaniesSection';
import styles from './ThemeOverviewTab.module.scss';

const TOP_COMPANIES_LIMIT = 10;

interface ThemeOverviewTabProps {
  entity?: Entity;
  theme: ThemeExposure | { theme: ThemeSchemaResponse };
  source: PosthogTrackingSource;
  title?: string;
  isPerfDrawer?: boolean;
  tooltipThemeId?: string;
}

export const ThemeOverviewTab = ({
  entity,
  theme,
  source,
  title,
  tooltipThemeId,
  isPerfDrawer = false,
}: ThemeOverviewTabProps) => {
  const isFollowedThemesFlagEnabled = useFlags(state =>
    state.getFeatureFlagState('followed_themes')
  );
  const {
    data = [],
    isFetching,
    error,
  } = useThemeCompanies({ themeId: theme.theme.id });

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          { styles: { width: '100%', height: '700px', borderRadius: '8px' } },
        ]}
      />
    );
  }

  if (error) {
    throw error;
  }

  const entityTags = data
    .map(item => item.company)
    .slice(0, TOP_COMPANIES_LIMIT);
  const slicedData = data.slice(0, TOP_COMPANIES_LIMIT);
  const remainingCount = Math.max(0, data.length - TOP_COMPANIES_LIMIT);

  return (
    <div className={styles.root}>
      {isPerfDrawer ? (
        <ThemeHeaderDetailed theme={theme.theme} title={title} />
      ) : (
        <StepperSection
          entity={entity}
          theme={theme}
          title={title}
          activeIndex={1}
          description={theme.theme.description}
          rightHeaderNode={
            isFollowedThemesFlagEnabled ? (
              <FollowThemeButton themeId={theme.theme.id} />
            ) : null
          }
        />
      )}
      <ErrorBoundaryWrapper>
        <ThemePerformanceSection
          entityTags={entityTags}
          themeId={theme.theme.id}
        />
      </ErrorBoundaryWrapper>
      {!!slicedData.length && (
        <>
          <div className={styles.horizontalDivider} />
          <TopCompaniesSection
            companies={slicedData}
            remainingCount={remainingCount}
            source={source}
            tooltipThemeId={tooltipThemeId}
          />
        </>
      )}
    </div>
  );
};
